import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, where, onSnapshot, orderBy, limit } from "firebase/firestore";
import { 
  Users, 
  GraduationCap, 
  DollarSign, 
  Calendar, 
  TrendingUp, 
  AlertCircle,
  CheckCircle,
  Clock,
  Star,
  BookOpen,
  Car,
  UserCheck,
  Activity,
  Filter,
  Download,
  CreditCard,
  Award
} from "lucide-react";

interface Student {
  id?: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  registrationType?: "online" | "walk-in";
  createdAt: any;
  role: string;
  branch: string;
}

interface Enrollment {
  id: string;
  studentId: string;
  courseId: string;
  courseFee: number;
  amountReceived: number;
  balance: number;
  status: "pending" | "approved" | "rejected" | "partial_payment" | "completed";
  enrolledAt: any;
  registrationType: "online" | "walk-in";
  paymentReceiptUrl?: string;
  branch: string;
  licenseCodes?: string[];
}

interface Course {
  id: string;
  title: string;
  price: number;
  duration?: number;
}

interface Schedule {
  id: string;
  studentId: string;
  courseId: string;
  enrollmentId: string;
  instructorId: string;
  date: string;
  status: "scheduled" | "completed" | "cancelled";
  branch: string;
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  branch: string;
}

interface Vehicle {
  id: string;
  licensePlate: string;
  status: string;
  branch: string;
}

interface Branch {
  id: string;
  name: string;
  address?: string;
}

interface LicenseCode {
  id: string;
  code: string;
  description: string;
  price: number;
  isActive?: boolean;
}

interface LicenseCodePayment {
  id: string;
  studentId: string;
  enrollmentId: string;
  licenseCodeId: string;
  amount: number;
  paymentMethod: string;
  paidAt: any;
  branch: string;
  notes?: string;
}

const Overview: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [licenseCodes, setLicenseCodes] = useState<LicenseCode[]>([]);
  const [licenseCodePayments, setLicenseCodePayments] = useState<LicenseCodePayment[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Enhanced filtering
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [selectedMonth, setSelectedMonth] = useState<string>(new Date().getMonth().toString());
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  
  // Instructor performance filter
  const [instructorPerformanceFilter, setInstructorPerformanceFilter] = useState<string>("completed");
  
  // Instructor performance pagination
  const [instructorCurrentPage, setInstructorCurrentPage] = useState<number>(1);
  const [instructorItemsPerPage] = useState<number>(5);

  // Helper function to safely format numbers
  const formatCurrency = (value: number | undefined | null): string => {
    const safeValue = Number(value) || 0;
    return safeValue.toLocaleString();
  };

  // Helper function to filter data by branch, month, and year
  const filterData = (data: any[], dateField: string = "enrolledAt", branchField: string = "branch") => {
    return data.filter(item => {
      // Branch filter
      if (selectedBranch !== "all" && item[branchField] !== selectedBranch) {
        return false;
      }
      
      // Date filter
      const itemDate = item[dateField]?.toDate?.() || new Date(item[dateField]);
      if (!itemDate) return selectedBranch !== "all" ? item[branchField] === selectedBranch : true;
      
      const itemMonth = itemDate.getMonth();
      const itemYear = itemDate.getFullYear();
      
      return itemMonth === parseInt(selectedMonth) && itemYear === parseInt(selectedYear);
    });
  };

  const exportData = () => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      selectedBranch,
      selectedMonth,
      selectedYear,
      stats: {
        totalStudents: stats.totalStudents,
        totalEnrollments: stats.totalEnrollments,
        totalRevenue: stats.totalRevenue,
        enrollmentRevenue: stats.enrollmentRevenue,
        licenseCodeRevenue: stats.licenseCodeRevenue,
        outstandingBalance: stats.outstandingBalance,
        enrollmentsByStatus: {
          pending: stats.pendingEnrollments,
          approved: stats.approvedEnrollments,
          partialPayment: stats.partialPaymentEnrollments,
          completed: stats.completedEnrollments,
          rejected: stats.rejectedEnrollments
        }
      },
      coursePerformance: courseEnrollmentStats,
      instructorPerformance: instructorPerformanceStats,
      recentEnrollments: recentEnrollments
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `driving-school-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch Students
        const studentsRef = collection(db, "users");
        const studentsQuery = query(studentsRef, where("role", "==", "student"));
        const unsubscribeStudents = onSnapshot(studentsQuery, (snapshot) => {
          const studentsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Student[];
          setStudents(studentsData);
        });

        // Fetch Enrollments
        const enrollmentsRef = collection(db, "enrollments");
        const unsubscribeEnrollments = onSnapshot(enrollmentsRef, (snapshot) => {
          const enrollmentsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Enrollment[];
          setEnrollments(enrollmentsData);
        });

        // Fetch Courses
        const coursesRef = collection(db, "courses");
        const unsubscribeCourses = onSnapshot(coursesRef, (snapshot) => {
          const coursesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Course[];
          setCourses(coursesData);
        });

        // Fetch Schedules
        const schedulesRef = collection(db, "schedules");
        const unsubscribeSchedules = onSnapshot(schedulesRef, (snapshot) => {
          const schedulesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Schedule[];
          setSchedules(schedulesData);
        });

        // Fetch Instructors
        const instructorsRef = collection(db, "users");
        const instructorsQuery = query(instructorsRef, where("role", "==", "instructor"));
        const unsubscribeInstructors = onSnapshot(instructorsQuery, (snapshot) => {
          const instructorsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Instructor[];
          setInstructors(instructorsData);
        });

        // Fetch Vehicles
        const vehiclesRef = collection(db, "vehicles");
        const unsubscribeVehicles = onSnapshot(vehiclesRef, (snapshot) => {
          const vehiclesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Vehicle[];
          setVehicles(vehiclesData);
        });

        // Fetch Branches
        const branchesRef = collection(db, "branches");
        const unsubscribeBranches = onSnapshot(branchesRef, (snapshot) => {
          const branchesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Branch[];
          setBranches(branchesData);
        });

        // Fetch License Codes
        const licenseCodesRef = collection(db, "licenseCodes");
        const unsubscribeLicenseCodes = onSnapshot(licenseCodesRef, (snapshot) => {
          const licenseCodesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as LicenseCode[];
          setLicenseCodes(licenseCodesData);
        });

        // Fetch License Code Payments
        const licenseCodePaymentsRef = collection(db, "licenseCodePayments");
        const unsubscribeLicenseCodePayments = onSnapshot(licenseCodePaymentsRef, (snapshot) => {
          const licenseCodePaymentsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as LicenseCodePayment[];
          setLicenseCodePayments(licenseCodePaymentsData);
        });

        setLoading(false);

        // Cleanup function
        return () => {
          unsubscribeStudents();
          unsubscribeEnrollments();
          unsubscribeCourses();
          unsubscribeSchedules();
          unsubscribeInstructors();
          unsubscribeVehicles();
          unsubscribeBranches();
          unsubscribeLicenseCodes();
          unsubscribeLicenseCodePayments();
        };
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter data based on branch, month, and year
  const filteredEnrollments = filterData(enrollments, "enrolledAt", "branch");
  const filteredStudents = filterData(students, "createdAt", "branch");
  const filteredSchedules = filterData(schedules, "date", "branch");
  const filteredLicenseCodePayments = filterData(licenseCodePayments, "paidAt", "branch");

  // Calculate statistics with filtered data
  const stats = {
    totalStudents: filteredStudents.length,
    onlineStudents: filteredStudents.filter(s => s.registrationType === "online").length,
    walkInStudents: filteredStudents.filter(s => s.registrationType === "walk-in").length,
    totalInstructors: instructors.filter(i => selectedBranch === "all" || i.branch === selectedBranch).length,
    totalVehicles: vehicles.filter(v => selectedBranch === "all" || v.branch === selectedBranch).length,
    activeVehicles: vehicles.filter(v => v.status === "active" && (selectedBranch === "all" || v.branch === selectedBranch)).length,
    
    // Enrollment stats (using filtered data)
    totalEnrollments: filteredEnrollments.length,
    pendingEnrollments: filteredEnrollments.filter(e => e.status === "pending").length,
    approvedEnrollments: filteredEnrollments.filter(e => e.status === "approved").length,
    partialPaymentEnrollments: filteredEnrollments.filter(e => e.status === "partial_payment").length,
    completedEnrollments: filteredEnrollments.filter(e => e.status === "completed").length,
    rejectedEnrollments: filteredEnrollments.filter(e => e.status === "rejected").length,
    
    // Financial stats (using filtered data)
    enrollmentRevenue: filteredEnrollments.reduce((sum, e) => sum + (Number(e.amountReceived) || 0), 0),
    licenseCodeRevenue: filteredEnrollments.reduce((sum, e) => sum + (Number(e.licenseCodeTotalPrice) || 0), 0),
    totalRevenue: filteredEnrollments.reduce((sum, e) => sum + (Number(e.amountReceived) || 0), 0) + 
                  filteredEnrollments.reduce((sum, e) => sum + (Number(e.licenseCodeTotalPrice) || 0), 0),
    pendingRevenue: filteredEnrollments.filter(e => e.status === "pending").reduce((sum, e) => sum + (Number(e.courseFee) || 0), 0),
    outstandingBalance: filteredEnrollments.reduce((sum, e) => sum + (Number(e.balance) || 0), 0),
    
    // Schedule stats
    totalSessions: filteredSchedules.length,
    completedSessions: filteredSchedules.filter(s => s.status === "completed").length,
    scheduledSessions: filteredSchedules.filter(s => s.status === "scheduled").length,
    cancelledSessions: filteredSchedules.filter(s => s.status === "cancelled").length,
    
    // License code stats
    totalAssignedLicenseCodeValue: filteredEnrollments.reduce((sum, e) => sum + (Number(e.licenseCodeTotalPrice) || 0), 0),
  };

  // Recent data using filtered enrollments
  const recentEnrollments = filteredEnrollments
    .sort((a, b) => (b.enrolledAt?.toDate?.() || new Date()).getTime() - (a.enrolledAt?.toDate?.() || new Date()).getTime())
    .slice(0, 5)
    .map(enrollment => {
      const student = students.find(s => s.id === enrollment.studentId);
      const course = courses.find(c => c.id === enrollment.courseId);
      return {
        ...enrollment,
        studentName: student ? `${student.firstName} ${student.lastName}` : 'Unknown',
        courseTitle: course?.title || 'Unknown Course'
      };
    });

  const pendingPayments = filteredEnrollments
    .filter(e => e.status === "pending" && e.paymentReceiptUrl)
    .sort((a, b) => (b.enrolledAt?.toDate?.() || new Date()).getTime() - (a.enrolledAt?.toDate?.() || new Date()).getTime())
    .slice(0, 5)
    .map(enrollment => {
      const student = students.find(s => s.id === enrollment.studentId);
      const course = courses.find(c => c.id === enrollment.courseId);
      return {
        ...enrollment,
        studentName: student ? `${student.firstName} ${student.lastName}` : 'Unknown',
        courseTitle: course?.title || 'Unknown Course'
      };
    });

  const courseEnrollmentStats = courses.map(course => {
    const courseEnrollments = filteredEnrollments.filter(e => e.courseId === course.id);
    return {
      ...course,
      enrollmentCount: courseEnrollments.length,
      revenue: courseEnrollments.reduce((sum, e) => sum + (Number(e.amountReceived) || 0), 0),
      approvedCount: courseEnrollments.filter(e => e.status === "approved").length,
      completedCount: courseEnrollments.filter(e => e.status === "completed").length
    };
  }).sort((a, b) => b.enrollmentCount - a.enrollmentCount);

  // Calculate instructor performance stats
  const instructorPerformanceStats = instructors.map(instructor => {
    const instructorSchedules = filterData(schedules, "scheduledAt", "branch")
      .filter(s => s.instructorId === instructor.id);
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const monthlySchedules = instructorSchedules.filter(schedule => {
      const scheduleDate = schedule.scheduledAt?.toDate?.() || new Date(schedule.scheduledAt);
      const filterMonth = parseInt(selectedMonth);
      const filterYear = parseInt(selectedYear);
      
      return scheduleDate.getMonth() === filterMonth && 
             scheduleDate.getFullYear() === filterYear;
    });
    
    return {
      ...instructor,
      totalSessions: instructorSchedules.length,
      monthlySessions: monthlySchedules.length,
      completedSessions: monthlySchedules.filter(s => s.status === "completed").length,
      scheduledSessions: monthlySchedules.filter(s => s.status === "scheduled").length,
      cancelledSessions: monthlySchedules.filter(s => s.status === "cancelled").length,
      completionRate: monthlySchedules.length > 0 ? 
        (monthlySchedules.filter(s => s.status === "completed").length / monthlySchedules.length) * 100 : 0
    };
  })
  .filter(instructor => {
    // Apply instructor performance filter
    if (instructorPerformanceFilter === "all") return true;
    if (instructorPerformanceFilter === "completed") return instructor.completedSessions > 0;
    if (instructorPerformanceFilter === "scheduled") return instructor.scheduledSessions > 0;
    if (instructorPerformanceFilter === "cancelled") return instructor.cancelledSessions > 0;
    return true;
  })
  .sort((a, b) => {
    // Sort based on the selected filter
    if (instructorPerformanceFilter === "completed") return b.completedSessions - a.completedSessions;
    if (instructorPerformanceFilter === "scheduled") return b.scheduledSessions - a.scheduledSessions;
    if (instructorPerformanceFilter === "cancelled") return b.cancelledSessions - a.cancelledSessions;
    return b.monthlySessions - a.monthlySessions; // Default: sort by total monthly sessions
  });

  // Instructor performance pagination
  const instructorTotalPages = Math.ceil(instructorPerformanceStats.length / instructorItemsPerPage);
  const instructorStartIndex = (instructorCurrentPage - 1) * instructorItemsPerPage;
  const instructorEndIndex = instructorStartIndex + instructorItemsPerPage;
  const paginatedInstructors = instructorPerformanceStats.slice(instructorStartIndex, instructorEndIndex);

  // Instructor pagination functions
  const handleInstructorNextPage = () => {
    if (instructorCurrentPage < instructorTotalPages) {
      setInstructorCurrentPage(instructorCurrentPage + 1);
    }
  };

  const handleInstructorPrevPage = () => {
    if (instructorCurrentPage > 1) {
      setInstructorCurrentPage(instructorCurrentPage - 1);
    }
  };

  const handleInstructorPageChange = (page: number) => {
    setInstructorCurrentPage(page);
  };

  // Reset instructor pagination when filter changes
  React.useEffect(() => {
    setInstructorCurrentPage(1);
  }, [instructorPerformanceFilter, selectedBranch, selectedMonth, selectedYear]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Overview</h1>
          <p className="text-gray-600 mt-1">Comprehensive dashboard with enrollment reports and analytics</p>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-3 w-full lg:w-auto">
          {/* Filters */}
          <div className="flex flex-wrap lg:flex-nowrap items-center gap-3 lg:gap-4">
            <Filter className="h-4 w-4 text-gray-500 flex-shrink-0" />
            
            {/* Branch Filter */}
            <div className="flex items-center gap-2 whitespace-nowrap">
              <label className="text-sm font-medium">Branch:</label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="border rounded px-3 py-1 text-sm min-w-[120px]"
              >
                <option value="all">All Branches</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Month Filter */}
            <div className="flex items-center gap-2 whitespace-nowrap">
              <label className="text-sm font-medium">Month:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border rounded px-3 py-1 text-sm min-w-[100px]"
              >
                <option value="0">January</option>
                <option value="1">February</option>
                <option value="2">March</option>
                <option value="3">April</option>
                <option value="4">May</option>
                <option value="5">June</option>
                <option value="6">July</option>
                <option value="7">August</option>
                <option value="8">September</option>
                <option value="9">October</option>
                <option value="10">November</option>
                <option value="11">December</option>
              </select>
            </div>
            
            {/* Year Filter */}
            <div className="flex items-center gap-2 whitespace-nowrap">
              <label className="text-sm font-medium">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="border rounded px-3 py-1 text-sm min-w-[80px]"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={exportData}
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Filter Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-2 text-blue-800">
          <Calendar className="h-4 w-4" />
          <span className="text-sm font-medium">
            Showing data for: {(() => {
              const branchName = selectedBranch === "all" ? "All Branches" : 
                branches.find(b => b.id === selectedBranch)?.name || "Unknown Branch";
              const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
              ];
              const monthName = monthNames[parseInt(selectedMonth)];
              return `${branchName} - ${monthName} ${selectedYear}`;
            })()}
          </span>
        </div>
      </div>

      {/* First Row - 3 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Total Enrollments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalEnrollments}</div>
            <div className="text-xs text-muted-foreground mt-1">
              All course enrollments
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Online Enrollments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredEnrollments.filter(e => e.registrationType === "online").length}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Web registrations
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Walk-in Enrollments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredEnrollments.filter(e => e.registrationType === "walk-in").length}</div>
            <div className="text-xs text-muted-foreground mt-1">
              In-person registrations
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row - 3 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending Enrollments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingEnrollments}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Awaiting approval
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Enrollment Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₱{formatCurrency(stats.enrollmentRevenue)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Course fees received
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Outstanding Balances
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₱{formatCurrency(stats.outstandingBalance)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Unpaid enrollment balances
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enrollment Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Enrollment Status Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.pendingEnrollments}</div>
              <div className="text-sm text-muted-foreground">Pending Review</div>
              <Badge variant="secondary" className="mt-1 !bg-orange-100 !text-orange-700">
                Needs Action
              </Badge>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.approvedEnrollments}</div>
              <div className="text-sm text-muted-foreground">Approved</div>
              <Badge variant="secondary" className="mt-1 !bg-green-100 !text-green-700">
                Active
              </Badge>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.partialPaymentEnrollments}</div>
              <div className="text-sm text-muted-foreground">Partial Payment</div>
              <Badge variant="secondary" className="mt-1 !bg-yellow-100 !text-yellow-700">
                Payment Due
              </Badge>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.completedEnrollments}</div>
              <div className="text-sm text-muted-foreground">Completed</div>
              <Badge variant="secondary" className="mt-1 !bg-blue-100 !text-blue-700">
                Finished
              </Badge>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{stats.rejectedEnrollments}</div>
              <div className="text-sm text-muted-foreground">Rejected</div>
              <Badge variant="secondary" className="mt-1 !bg-red-100 !text-red-700">
                Declined
              </Badge>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="text-sm font-medium mb-2">Enrollment Distribution</div>
            <div className="w-full bg-gray-200 rounded-full h-2 flex overflow-hidden">
              <div 
                className="bg-orange-500 h-2" 
                style={{ width: `${stats.totalEnrollments > 0 ? (stats.pendingEnrollments / stats.totalEnrollments) * 100 : 0}%` }}
              ></div>
              <div 
                className="bg-green-500 h-2" 
                style={{ width: `${stats.totalEnrollments > 0 ? (stats.approvedEnrollments / stats.totalEnrollments) * 100 : 0}%` }}
              ></div>
              <div 
                className="bg-yellow-500 h-2" 
                style={{ width: `${stats.totalEnrollments > 0 ? (stats.partialPaymentEnrollments / stats.totalEnrollments) * 100 : 0}%` }}
              ></div>
              <div 
                className="bg-blue-500 h-2" 
                style={{ width: `${stats.totalEnrollments > 0 ? (stats.completedEnrollments / stats.totalEnrollments) * 100 : 0}%` }}
              ></div>
              <div 
                className="bg-red-500 h-2" 
                style={{ width: `${stats.totalEnrollments > 0 ? (stats.rejectedEnrollments / stats.totalEnrollments) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Course Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {courseEnrollmentStats.slice(0, 5).map((course, index) => (
              <div key={course.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{course.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {course.enrollmentCount} enrollments • ₱{course.revenue.toLocaleString()} revenue
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-sm font-medium text-green-600">{course.approvedCount}</div>
                    <div className="text-xs text-muted-foreground">Approved</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-blue-600">{course.completedCount}</div>
                    <div className="text-xs text-muted-foreground">Completed</div>
                  </div>
                  <Badge variant="outline">
                    #{index + 1}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Instructor Performance */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                Instructor Performance
              </CardTitle>
              <div className="text-sm text-muted-foreground">
                Sessions for {new Date(0, parseInt(selectedMonth)).toLocaleDateString('en-US', { month: 'long' })} {selectedYear}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={instructorPerformanceFilter}
                onChange={(e) => setInstructorPerformanceFilter(e.target.value)}
                className="border rounded px-3 py-1 text-sm min-w-[120px]"
              >
                <option value="all">All Sessions</option>
                <option value="completed">Completed</option>
                <option value="scheduled">Scheduled</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {paginatedInstructors.map((instructor, index) => (
              <div key={instructor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{instructor.firstName} {instructor.lastName}</div>
                  <div className="text-sm text-muted-foreground">
                    {instructor.monthlySessions} sessions this month • {instructor.totalSessions} total sessions
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-sm font-medium text-green-600">{instructor.completedSessions}</div>
                    <div className="text-xs text-muted-foreground">Completed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-blue-600">{instructor.scheduledSessions}</div>
                    <div className="text-xs text-muted-foreground">Scheduled</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-red-600">{instructor.cancelledSessions}</div>
                    <div className="text-xs text-muted-foreground">Cancelled</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-purple-600">{instructor.completionRate.toFixed(1)}%</div>
                    <div className="text-xs text-muted-foreground">Completion</div>
                  </div>
                  <Badge variant="outline">
                    #{instructorStartIndex + index + 1}
                  </Badge>
                </div>
              </div>
            ))}
            {instructorPerformanceStats.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No instructors with {instructorPerformanceFilter === "all" ? "session data" : `${instructorPerformanceFilter} sessions`} available for the selected period
              </div>
            )}

            {/* Instructor Performance Pagination */}
            {instructorPerformanceStats.length > 0 && instructorTotalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {instructorStartIndex + 1} to {Math.min(instructorEndIndex, instructorPerformanceStats.length)} of {instructorPerformanceStats.length} instructors
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleInstructorPrevPage}
                    disabled={instructorCurrentPage === 1}
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: instructorTotalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={instructorCurrentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleInstructorPageChange(page)}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleInstructorNextPage}
                    disabled={instructorCurrentPage === instructorTotalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Overview;
